import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';
import HormuzHubNav from '../../../components/HormuzHubNav';
import { chokepointsAPI } from '../../../services/api';
import {
  getOrganizationSchema,
  getBreadcrumbSchema,
  getHowToSchema,
} from '../../../utils/seoSchemas';
import { Calculator, TrendingUp, DollarSign, AlertCircle, Loader } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const HormuzOilImpactCalculator = () => {
  const [inputs, setInputs] = useState({
    disruptionPercent: 20,
    durationDays: 30,
    basePrice: 80,
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCalculate = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await chokepointsAPI.calculateImpact('hormuz', inputs);
      setResults(res.data);
    } catch (e) {
      setError('Failed to calculate impact. Please try again.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Generate chart data for visualization
  const chartData = results
    ? [
        { day: 0, price: inputs.basePrice, label: 'Base' },
        { day: inputs.durationDays, price: results.results.projectedPrice, label: 'Projected' },
      ]
    : [];

  const title = 'Strait of Hormuz Oil Impact Calculator | Scenario Price Impact Simulator';
  const description =
    'Calculate indicative oil price impacts from hypothetical disruptions in the Strait of Hormuz. Simulate partial shutdown scenarios and see how disruption size and duration could interact with base prices.';

  const howToSteps = [
    {
      name: 'Enter Disruption Percentage',
      text: 'Input the percentage of daily oil flow that would be disrupted (0-100%)',
    },
    {
      name: 'Set Duration',
      text: 'Specify how long the disruption would last in days (1-365)',
    },
    {
      name: 'Enter Base Price',
      text: 'Input the current or baseline oil price per barrel in USD',
    },
    {
      name: 'Calculate Impact',
      text: 'Click "Calculate Impact" to see projected price increases and economic effects',
    },
  ];

  const jsonLd = [
    getOrganizationSchema(),
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Strait of Hormuz', path: '/strait-of-hormuz' },
      { name: 'Oil Impact Calculator', path: '/strait-of-hormuz/oil-impact-calculator' },
    ]),
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Strait of Hormuz Oil Impact Calculator',
      description,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://traderadarglobal.com/strait-of-hormuz/oil-impact-calculator',
    },
    getHowToSchema(
      'How to Use the Strait of Hormuz Oil Impact Calculator',
      description,
      howToSteps
    ),
  ];

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title={title}
        description={description}
        path="/strait-of-hormuz/oil-impact-calculator"
        keywords="Hormuz oil impact calculator, oil price disruption calculator, Strait of Hormuz price impact"
        jsonLd={jsonLd}
        image="/api/og-image/hormuz-calculator.png"
        imageAlt="Strait of Hormuz Oil Impact Calculator - Price impact simulator"
      />

      <HormuzHubNav />

      {/* A. Overview */}
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-2">
          Oil Impact Calculator
        </h1>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-2">
          Last updated: March 2026 · This tool provides stylised, scenario‑based estimates only and
          is not a trading or investment system.
        </p>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant leading-relaxed">
          The calculator links disruption assumptions – how much flow is affected and for how long –
          to a simple price impact model. It is designed to help analysts think in ranges and
          sensitivities rather than point forecasts. For a narrative discussion of the same themes,
          see the{' '}
          <Link
            to="/strait-of-hormuz/economic-impact"
            className="text-primary-600 dark:text-primary underline"
          >
            Economic Impact
          </Link>{' '}
          page.
        </p>
      </header>

      {/* Scenario explainer */}
      <section className="mb-6 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-3">
          Example Scenarios You Can Test
        </h2>
        <ul className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant list-disc list-inside">
          <li>
            <strong>Short, limited disruption:</strong> 7 days with a 10–20% reduction in flows –
            useful for thinking about temporary incidents or near‑misses.
          </li>
          <li>
            <strong>Medium‑term stress:</strong> 30–60 days with a 30–50% reduction – a proxy for
            sustained tension with some rerouting in place.
          </li>
          <li>
            <strong>Severe case:</strong> 90+ days with a 70%+ disruption – a stress‑test for how
            extreme scenarios might affect price levels if spare capacity is limited.
          </li>
        </ul>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant mt-2">
          Use the inputs below to approximate these scenarios and compare the outputs to the
          qualitative ranges discussed in the{' '}
          <Link
            to="/strait-of-hormuz/economic-impact"
            className="text-primary-600 dark:text-primary underline"
          >
            Economic Impact
          </Link>{' '}
          analysis.
        </p>
      </section>

      {/* Calculator Form */}
      <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6 mb-8">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-2">
              Oil Flow Disruption (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={inputs.disruptionPercent}
              onChange={(e) =>
                setInputs({ ...inputs, disruptionPercent: parseFloat(e.target.value) || 0 })
              }
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-700"
            />
            <p className="text-xs text-slate-500 dark:text-on-surface-variant mt-1">
              Percentage of daily oil flow disrupted (0-100%)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-2">
              Duration (Days)
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={inputs.durationDays}
              onChange={(e) =>
                setInputs({ ...inputs, durationDays: parseInt(e.target.value) || 1 })
              }
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-700"
            />
            <p className="text-xs text-slate-500 dark:text-on-surface-variant mt-1">
              How long the disruption lasts (1-365 days)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-2">
              Base Oil Price ($/barrel)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={inputs.basePrice}
              onChange={(e) =>
                setInputs({ ...inputs, basePrice: parseFloat(e.target.value) || 0 })
              }
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-700"
            />
            <p className="text-xs text-slate-500 dark:text-on-surface-variant mt-1">
              Current or baseline oil price per barrel
            </p>
          </div>

          <button
            onClick={handleCalculate}
            disabled={loading}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="w-5 h-5 mr-2" />
                Calculate Impact
              </>
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-6">
          {/* Impact Summary */}
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-lg p-6 border border-primary-200 dark:border-primary-800">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-4">
              Projected Impact
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <ResultCard
                label="Price Increase %"
                value={`+${results.results.priceIncreasePercent}%`}
                icon={TrendingUp}
                color="text-green-600 dark:text-green-400"
              />
              <ResultCard
                label="Price Increase ($)"
                value={`+$${results.results.priceIncrease.toFixed(2)}`}
                icon={DollarSign}
                color="text-blue-600 dark:text-blue-400"
              />
              <ResultCard
                label="Projected Price"
                value={`$${results.results.projectedPrice.toFixed(2)}`}
                icon={AlertCircle}
                color="text-primary-600 dark:text-primary"
              />
            </div>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-4">
                Price Projection
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis domain={['auto', 'auto']} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    name="Price ($/barrel)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* AI Explanation */}
          {results.explanation && (
            <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-3">
                Impact Analysis
              </h3>
              <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
                {results.explanation}
              </p>
            </div>
          )}

          {/* Calculation Details */}
          <div className="bg-slate-100 dark:bg-surface-container-low rounded-lg p-6">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-on-surface mb-3">
              Calculation Details
            </h3>
            <div className="text-xs text-slate-600 dark:text-on-surface-variant space-y-2">
              <p>
                <strong>Enhanced Model:</strong> Price Impact = Base Price × (Elasticity × Duration Multiplier × Severity Factor × Disruption %)
              </p>
              <p>
                <strong>Base Elasticity:</strong> 0.4 (short-term oil demand elasticity)
              </p>
              <p>
                <strong>Duration Multiplier:</strong> {
                  inputs.durationDays > 30 ? '1.5x (long-term disruption)' :
                  inputs.durationDays > 7 ? '1.2x (medium-term)' :
                  '1.0x (short-term)'
                }
              </p>
              <p>
                <strong>Severity Factor:</strong> {
                  inputs.disruptionPercent >= 90 ? '3.0x (critical disruption)' :
                  inputs.disruptionPercent >= 75 ? '2.2x (severe)' :
                  inputs.disruptionPercent >= 50 ? '1.5x (major)' :
                  inputs.disruptionPercent >= 25 ? '1.0x (moderate)' :
                  inputs.disruptionPercent >= 10 ? '0.8x (minor)' :
                  '0.6x (minimal)'
                }
              </p>
              <p>
                <strong>Note:</strong> This model accounts for non-linear impacts where larger disruptions have exponentially greater effects on oil prices.
              </p>
              <p>
                <strong>Calculated:</strong> {new Date(results.calculatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Methodology & Usage Note */}
      <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
          <strong>Important:</strong> This calculator uses a simplified elasticity‑based model.
          Real‑world outcomes depend on many additional factors, including spare capacity, inventory
          policy, financial conditions, and geopolitical responses. Outputs are indicative only.
        </p>
        <p className="text-xs text-blue-900 dark:text-blue-200">
          Nothing here constitutes investment, trading, or policy advice. Use the results as one
          input among many when thinking about risk, and cross‑check with the{' '}
          <Link
            to="/strait-of-hormuz/statistics"
            className="underline text-blue-900 dark:text-blue-200"
          >
            Statistics
          </Link>{' '}
          and{' '}
          <Link
            to="/strait-of-hormuz/risk-dashboard"
            className="underline text-blue-900 dark:text-blue-200"
          >
            Risk Dashboard
          </Link>{' '}
          pages for context.
        </p>
      </div>
    </article>
  );
};

const ResultCard = ({ label, value, icon: Icon, color }) => (
  <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-4 dark:border-outline-variant/15 dark:bg-surface-container">
    <div className="flex items-center space-x-2 mb-2">
      {Icon && <Icon className={`w-4 h-4 ${color}`} />}
      <span className="text-xs text-slate-600 dark:text-on-surface-variant">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

export default HormuzOilImpactCalculator;

